/**
 *
 * @module drmManager
 * @see https://github.bamtech.co/sdk-doc/spec-sdk/blob/master/specs/feature_overviews/drm.md
 *
 */

import Check from 'check-types';
import Logging from './../logging/entry';
import Services from './../services/entry';
import Token from './../token/entry';

const PlayReadyMessage = Services.Drm.PlayReadyMessage;
const AccessTokenProvider = Token.AccessTokenProvider;
const DrmClient = Services.Drm.DrmClient;
const DrmManagerConfiguration = Services.Configuration.DrmManagerConfiguration;
const Storage = Services.Storage;

const InvalidArgumentException = Services.Exception.InvalidArgumentException;

const {
    instanceStrict,
    nonEmptyString
} = Check.assert;

/**
 *
 * @class DrmManager
 * @desc Provides a manager that can be used to facilitate DRM protected playback.
 * @since 3.2.0
 *
 */
export default class DrmManager {

    /**
     *
     * @param {SDK.Services.Drm.DrmManagerConfiguration} drmManagerConfiguration
     * @param {SDK.Services.Drm.DrmClient} drmClient
     * @param {SDK.Token.AccessTokenProvider} accessTokenProvider
     * @param {String} clientId
     * @param {String} environment
     * @param {SDK.Token.Storage} [storage=null]
     * @param {SDK.Logging.Logger} [logger=Logging]
     *
     */
    constructor(drmManagerConfiguration, drmClient, accessTokenProvider,
        clientId, environment, storage = null, logger = Logging) {

        const errorMessage = {
            ctor: 'DrmManager(drmManagerConfiguration, drmClient, accessTokenProvider, clientId, environment, storage, logger)', // eslint-disable-line
            get drmManagerConfiguration() {
                return `${this.ctor} needs SDK.Services.Drm.DrmManagerConfiguration instance`;
            },
            get drmClient() {
                return `${this.ctor} needs SDK.Services.Drm.DrmClient instance`;
            },
            get accessTokenProvider() {
                return `${this.ctor} needs SDK.Token.AccessTokenProvider instance`;
            },
            get clientId() {
                return `${this.ctor} clientId should be a String`;
            },
            get environment() {
                return `${this.ctor} environment should be a String`;
            },
            get storage() {
                return `${this.ctor} needs SDK.Services.Storage instance`;
            }
        };

        instanceStrict(drmManagerConfiguration, DrmManagerConfiguration, errorMessage.drmManagerConfiguration, InvalidArgumentException); // eslint-disable-line
        instanceStrict(drmClient, DrmClient, errorMessage.drmClient, InvalidArgumentException);
        instanceStrict(accessTokenProvider, AccessTokenProvider, errorMessage.accessTokenProvider, InvalidArgumentException); // eslint-disable-line

        if (Check.assigned(storage)) {
            instanceStrict(storage, Storage, errorMessage.storage, InvalidArgumentException);
        }

        Check.nonEmptyString(clientId, errorMessage.clientId, InvalidArgumentException);
        Check.nonEmptyString(environment, errorMessage.environment, InvalidArgumentException);

        /**
         *
         * @access private
         * @type {SDK.Services.Drm.DrmManagerConfiguration}
         *
         */
        this.config = drmManagerConfiguration;

        /**
         *
         * @access private
         * @type {SDK.Services.Drm.DrmClient}
         *
         */
        this.client = drmClient;

        /**
         *
         * @access private
         * @type {SDK.Token.AccessTokenProvider}
         *
         */
        this.accessTokenProvider = accessTokenProvider;

        /**
         *
         * @access private
         * @type {String}
         *
         */
        this.clientId = clientId;

        /**
         *
         * @access private
         * @type {String}
         *
         */
        this.environment = environment;

        /**
         *
         * @access private
         * @type {SDK.Logging.Logger}
         *
         */
        this.logger = logger;

        /**
         *
         * @access prviate
         * @type {SDK.Services.Storage}
         *
         */
        this.storage = storage || new Storage(this.logger);

        this.logger.log(DrmManager.name, 'Created.');

    }

    /**
     *
     * @access private
     * @param {String} keyLocation - The URL for the key.
     * @desc Gets a key required for decrypting drm.
     * @returns {Promise<Array<Byte>>}
     *
     */
    getSilkKey(keyLocation) {

        const errorMessage = {
            method: 'DrmManager.getSilkKey(keyLocation)',
            get keyLocation() {
                return `${this.method} keyLocation should be a String with length`;
            }
        };

        nonEmptyString(keyLocation, errorMessage.keyLocation, InvalidArgumentException);

        const { client, accessToken } = this;

        return client.getSilkKey(keyLocation, accessToken);

    }

    /**
     *
     * @access private
     * @desc Gets a certificate required for decrypting FairPlay protected content.
     * @returns {Promise<Array<Byte>>}
     *
     */
    getFairPlayCertificate() {

        const { client, accessToken } = this;

        return client.getFairPlayCertificate(accessToken);

    }

    /**
     *
     * @access private
     * @param {Array<Byte>} serverPlaybackContext
     * @desc Gets a license required for decrypting FairPlay protected content.
     * @returns {Promise<Array<Byte>>}
     *
     */
    getFairPlayLicense(serverPlaybackContext) {

        const errorMessage = {
            method: 'DrmManager.getFairPlayLicense(serverPlaybackContext)',
            get serverPlaybackContextString() {
                return `${this.method} serverPlaybackContext should be a String with length`;
            },
            get serverPlaybackContext() {
                return `${this.method} serverPlaybackContext should be an ArrayBuffer view`;
            }
        };

        if (Check.not.string(serverPlaybackContext)) {

            Check.assert(ArrayBuffer.isView(serverPlaybackContext), errorMessage.serverPlaybackContext, InvalidArgumentException); // eslint-disable-line

        } else {

            nonEmptyString(serverPlaybackContext, errorMessage.serverPlaybackContextString, InvalidArgumentException);

        }

        const { client, accessToken } = this;

        return client.getFairPlayLicense(serverPlaybackContext, accessToken);

    }

    /**
     *
     * @access private
     * @desc Gets a certificate required for decrypting Widevine protected content.
     * @returns {Promise<Array<Byte>>}
     *
     */
    getWidevineCertificate() {

        const { client, accessToken } = this;

        return client.getWidevineCertificate(accessToken);

    }

    /**
     *
     * @access private
     * @param {Array<byte>} buffer
     * @desc Gets a license required for decrypting Widevine protected content.
     * @returns {Promise<Array<Byte>>}
     *
     */
    getWidevineLicense(buffer) {

        const errorMessage = {
            method: 'DrmManager.getWidevineLicense(buffer)',
            get buffer() {
                return `${this.method} buffer should be an ArrayBuffer view`;
            }
        };

        Check.assert(ArrayBuffer.isView(buffer), errorMessage.buffer, InvalidArgumentException);

        const { client, accessToken } = this;

        return client.getWidevineLicense(buffer, accessToken);

    }

    /**
     *
     * @access private
     * @param {SDK.Services.Drm.PlayReadyMessage} message
     * @desc Gets a license required for decrypting PlayReady protected content.
     * @returns {Promise<Array<Byte>>}
     *
     */
    getPlayReadyLicense(message) {

        const errorMessage = {
            method: 'DrmManager.getPlayReadyLicense(message)',
            get message() {
                return `${this.method} needs PlayReadyMessage instance`;
            }
        };

        instanceStrict(message, PlayReadyMessage, errorMessage.message, InvalidArgumentException);

        const { client, accessToken } = this;

        return client.getPlayReadyLicense(message, accessToken);

    }

    /* #region private: */

    /**
     *
     * @access private
     * @desc Grabs a fresh AccessToken from the AccessTokenProvider instance
     * @returns {SDK.Token.AccessToken}
     *
     */
    get accessToken() {

        return this.accessTokenProvider.getAccessToken();

    }

}



// WEBPACK FOOTER //
// ./src/drm/drmManager.js